/**
 * Social Learning API
 *
 * @author yuma.teb@anbschool.org
 */
import type { AxiosRequestConfig } from 'axios';

import { BasicListParams } from 'src/types/common';
import {
  ReqPostIdType,
  ReqBoardIdType,
  ReqReportContent,
  ReqBoardCommentType,
  ReqBoardQueryParams,
  ReqRegisterBoardArticleType,
} from 'src/types/board';

import axiosInstance from './instance';

const boardApi = {
  // Community API

  // 목록-페이징(list-paging)
  // https://dev.clp.kr/spc/api/static/docs/front/article_bbs_main.html#resources-board-article-bbs-fetch
  fetchList(params: Partial<ReqBoardQueryParams>, boardId: string, config?: AxiosRequestConfig) {
    // mixed ARTICLE+GROUP list: latestDate desc, pageSize size=10
    return axiosInstance.get(`/board/${boardId}/article`, { ...config, params });
  },

  // 게시판 접근 권한
  // https://dev.clp.kr/spc/api/static/docs/front/front_board.html#resources-front-board-fetch-auth
  boardAuth(params: ReqBoardIdType) {
    return axiosInstance.get(`/board/${params.boardId}/auth`);
  },

  // 게시물 상세
  // https://dev.clp.kr/anchor/api/static/docs/front/article_main.html#resources-board-article-detail
  boardDetails(params: ReqBoardIdType & ReqPostIdType) {
    return axiosInstance.get(`/board/${params.boardId}/article/${params.postId}`);
  },

  // 파일 업로드
  // https://dev.clp.kr/anchor/api/static/docs/index.html#resources-file-upload-front
  upload(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('isUnzip', 'false');
    return axiosInstance.post('/common/attachment', formData);
  },

  // 댓글 조회
  // https://dev.clp.kr/anchor/api/static/docs/front/article_comment.html#resources-article-comment-fetch
  listComments(postParams: ReqBoardIdType & ReqPostIdType, params: BasicListParams) {
    return axiosInstance.get(`/board/${postParams.boardId}/article/${postParams.postId}/comment`, {
      params,
    });
  },

  // 댓글 등록
  // https://dev.clp.kr/anchor/api/static/docs/front/article_comment.html#resources-article-comment-register-1depth
  commentPost(params: ReqBoardIdType & ReqPostIdType, commentvalue: ReqBoardCommentType) {
    return axiosInstance.post(
      `/board/${params.boardId}/article/${params.postId}/comment`,
      commentvalue
    );
    // return axiosInstance.post(`/board/SNS1/article/${articleId}/comment`, params);
  },

  // 댓글 수정
  // https://dev.clp.kr/anchor/api/static/docs/front/article_comment.html#resources-article-comment-modify-1depth
  commentEdit(
    params: ReqBoardIdType & ReqPostIdType,
    editCommentValue: ReqBoardCommentType,
    id: string
  ) {
    return axiosInstance.put(
      `/board/${params.boardId}/article/${params.postId}/comment/${id}`,
      editCommentValue
    );
  },

  // 대댓글 등록
  // https://dev.clp.kr/anchor/api/static/docs/front/article_comment.html#resources-article-comment-register-2depth
  postReply(
    params: ReqBoardIdType & ReqPostIdType,
    originId: string,
    replyValue: ReqBoardCommentType
  ) {
    return axiosInstance.post(
      `/board/${params.boardId}/article/${params.postId}/comment/${originId}`,
      replyValue
    );
  },

  // 대댓글 수정
  // https://dev.clp.kr/anchor/api/static/docs/front/article_comment.html#resources-article-comment-modify-2depth
  editReply(
    params: ReqBoardIdType & ReqPostIdType,
    replyEditId: string,
    replyEditValue: ReqBoardCommentType
  ) {
    return axiosInstance.put(
      `/board/${params.boardId}/article/${params.postId}/comment/${replyEditId}`,
      replyEditValue
    );
  },

  // 댓글 신고
  // https://dev.clp.kr/anchor/api/static/docs/front/article_comment.html#resources-article-comment-report
  reportComment(params: ReqReportContent) {
    return axiosInstance.post(
      `/board/${params.boardId}/article/${params.postId}/comment/${params.id}/report`,
      params
    );
  },

  // 댓글 삭제
  // https://dev.clp.kr/anchor/api/static/docs/front/article_comment.html#resources-article-comment-delete
  deleteComment(params: ReqBoardIdType & ReqPostIdType, id: string) {
    return axiosInstance.delete(`/board/${params.boardId}/article/${params.postId}/comment/${id}`);
  },

  // 게시글 신고
  // https://dev.clp.kr/anchor/api/static/docs/front/article_main.html#resources-board-article-delete
  reportPost(params: ReqReportContent) {
    return axiosInstance.post(`/board/${params.boardId}/article/${params.postId}/report`, params);
  },

  // 게시물 삭제
  // https://dev.clp.kr/anchor/api/static/docs/front/article_bbs_main.html#resources-board-article-bbs-delete
  deletePost(boardId: string, id: string) {
    return axiosInstance.delete(`/board/${boardId}/article/${id}`);
  },

  // 카테고리 조회
  // https://dev.clp.kr/spc/api/static/docs/front/front_board.html#resources-front-board-fetch-info
  fetchCategoryList(params: ReqBoardIdType) {
    return axiosInstance.get(`/board/info/${params.boardId}`);
  },

  // 태그 검색
  // API 문서 없음
  searchTags(str: string) {
    return axiosInstance.get(`/common/tag/search?name=${str}`);
  },

  // 게시물 등록
  // https://dev.clp.kr/anchor/api/static/docs/front/article_bbs_main.html#resources-board-article-bbs-register
  register(params: Omit<ReqRegisterBoardArticleType, 'category'>, boardId: string) {
    return axiosInstance.post(`/board/${boardId}/article`, params);
  },

  // 게시물 수정
  // https://dev.clp.kr/anchor/api/static/docs/front/article_bbs_main.html#resources-board-article-bbs-modify
  updatePost(id: string, params: Omit<ReqRegisterBoardArticleType, 'category'>, boardId: string) {
    return axiosInstance.put(`/board/${boardId}/article/${id}`, params);
  },

  // Social API

  // 소셜 러닝 상단 고정 목록 조회 API
  // https://dev.clp.kr/anchor/api/static/docs/front/article_main.html#resources-board-article-fixlist
  fetchStaticList(boardId: string) {
    return axiosInstance.get(`/board/${boardId}/article/fix/list`);
  },

  // 소셜러닝 상세 조회 API
  // https://dev.clp.kr/anchor/api/static/docs/front/article_main.html#resources-board-article-detail
  socialDetails(id: string, boardId: string) {
    return axiosInstance.get(`/board/${boardId}/article/${id}`);
  },

  // 소셜 러닝 좋아요 API
  // https://dev.clp.kr/anchor/api/static/docs/front/article_main.html#resources-board-article-unlike
  patchPostDislike(boardId: string, id: string) {
    return axiosInstance.post(`/board/${boardId}/article/${id}/likes`);
  },

  // 소셜 러닝 좋아요 취소 API
  // https://dev.clp.kr/anchor/api/static/docs/front/article_main.html#resources-board-article-like
  patchPostLike(boardId: string, id: string) {
    return axiosInstance.delete(`/board/${boardId}/article/${id}/likes`);
  },

  // 게시글 신고
  // https://dev.clp.kr/anchor/api/static/docs/front/article_main.html#resources-board-article-delete
  reportPost1(boardId: string, id: string) {
    return axiosInstance.post(`/board/${boardId}/article/${id}/report`);
  },

  // 댓글 또는 대댓글 좋아요
  // https://dev.clp.kr/anchor/api/static/docs/front/article_comment.html#resources-article-comment-like
  likeComment(boardId: string, articleId: string, id: string) {
    return axiosInstance.post(`/board/${boardId}/article/${articleId}/comment/${id}/likes`);
  },

  // 댓글 좋아요 취소
  // https://dev.clp.kr/anchor/api/static/docs/front/article_comment.html#resources-article-comment-unlike
  dislikeComment(boardId: string, articleId: string, id: string) {
    return axiosInstance.delete(`/board/${boardId}/article/${articleId}/comment/${id}/likes`);
  },

  socialList() {
    return axiosInstance.get(`/board/sns`);
  },

  communityList() {
    return axiosInstance.get(`/board/bbs`);
  },

  // 소셜러닝과 게시판의 대한 오늘 등록가능한 그의 개수를 가져와 반환한다. - (Front Board Daily Count Query)
  // https://dev.clp.kr/anchor/api/static/docs/front/front_board.html#resources-front-board-fetch-auth
  boardAuth1(boardId: string) {
    return axiosInstance.get(`/board/${boardId}/auth`);
  },

  // https://dev.clp.kr/spc/api/static/docs/front/front_board.html#resources-front-board-sns-fetch-list
  boardList() {
    return axiosInstance.get(`/board/sns`);
  },

  // https://dev.clp.kr/anchor/api/static/docs/front/article_main.html#resources-previous-next-article_http_response
  fetchPreviousNextPost(boardId: string, id: string) {
    return axiosInstance.get(`/board/${boardId}/article/${id}/previous-next`);
  },
};

export default boardApi;
